import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Typography } from "@mui/material";
import { Check, Phone } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { Callout, Checkbox, InfoBanner, OtpInput, PageHeader, StepActions } from "@components/ui_v2";
import { ConfirmationDialog } from "@components/ConexionSat/ConfirmationDialog";
import { CustomTextCiecBuro } from "@components/CustomTextCiecBuro";
import { RootState, useAppDispatch } from "@store/store";
import { setBuroNip, setBuroNipLastCodeRequest, setCheckSatBureau } from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useRequestPhoneCodeMutation, useValidatePhoneCodeMutation } from "@api/onboarding";
import { useLazyGetPersonByIdQuery, useLazyGetPersonsQuery } from "@api/personasApi";
import { useBuroValidatorMutation } from "@api/sat_apis_new/satApsi";
import { usePostValidateNextStageMutation } from "@api/applications";
import { ApiError } from "@interfaces/response";
import { NipBuroVariant } from "@interfaces/nipBuro";
import { PATHS_TOFU_V2 } from "@helpers/constantes";
import { formatPhoneNumber, formatTime, getRemainingTime } from "@helpers/funciones";
import { NIP_BURO_CONFIG, buildBuroPayloadPfae, getNipBuroPhone } from "@helpers/nipBuroHelpers";
import useStatusConfig from "@hooks/useStatusConfig";

const TIME_LIMIT = 120;

interface Props {
  variant: NipBuroVariant;
}

/**
 * "Autoriza la consulta a Buró de Crédito": NIP por SMS/WhatsApp, autorización (reingresando el código,
 * como el modal legacy) y consulta a Buró. Por ahora solo PFAE (legacy `tofu/pm/ConsultaBuroNipPfae` +
 * `ModalBuroNipPfae`); las demás personas se agregan como variantes al migrarlas (`NipBuroVariant`).
 */
export const NipBuro = ({ variant }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { screenStatus, checkSatBureau } = NIP_BURO_CONFIG[variant];
  const { nextStatus, previousStatus } = useStatusConfig(screenStatus);

  const company = useSelector((state: RootState) => state.register.company);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const application = useSelector((state: RootState) => state.register.application);
  const buroNip = useSelector((state: RootState) => state.register.buroNip);

  const [validatePhoneCode] = useValidatePhoneCodeMutation();
  const [requestPhoneCode, { isLoading: isRequestingCode }] = useRequestPhoneCodeMutation();
  const [getPersons] = useLazyGetPersonsQuery();
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [buroValidator] = useBuroValidatorMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const resendEffectRanRef = useRef(false);

  const [otp, setOtp] = useState("");
  const [modalOtp, setModalOtp] = useState("");
  const [secondsRemaining, setSecondsRemaining] = useState(TIME_LIMIT);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  const [justResent, setJustResent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const phone = getNipBuroPhone(company);
  const otpConfirmCorrecto = modalOtp.length === 4 && modalOtp === buroNip.code;

  useEffect(() => {
    if (secondsRemaining <= 0) return;
    const t = setTimeout(() => setSecondsRemaining((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsRemaining]);

  // Si el último envío fue hace menos de 2 min, solo se retoma la cuenta (como `ConsultaBuroNipPF`).
  const handleResendCode = async () => {
    try {
      setIsLoading(true);
      setRateLimited(false);

      if (buroNip.isValid || isRequestingCode || !phone.countryCode || !phone.number) return;

      const secondsToWait = getRemainingTime(buroNip.last_code_request || "", TIME_LIMIT);
      if (secondsToWait > 0) {
        setSecondsRemaining(secondsToWait);
        return;
      }

      dispatch(setBuroNipLastCodeRequest({ last_code_request: dayjs().toISOString() }));
      try {
        await requestPhoneCode({ country_code: phone.countryCode, phone_number: phone.number }).unwrap();
      } catch (requestError) {
        if ((requestError as ApiError)?.status !== 429) {
          dispatch(setBuroNipLastCodeRequest({ last_code_request: null }));
        }
        throw requestError;
      }

      setSecondsRemaining(TIME_LIMIT);
      setJustResent(true);
      setTimeout(() => setJustResent(false), 4000);
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 429) {
        setRateLimited(true);
        return;
      }
      Sentry.captureException(error, { extra: { component: `NipBuro-${variant}-resend` } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  const validateCode = async () => {
    try {
      if (buroNip.isValid) {
        setErrorMessage("");
        return;
      }
      if (otp.length < 4) return;
      if (!phone.countryCode || !phone.number) {
        setErrorMessage("No se encontró el número de teléfono");
        return;
      }

      setIsLoading(true);
      setErrorMessage("");

      await validatePhoneCode({ country_code: phone.countryCode, phone_number: phone.number, code: otp }).unwrap();

      dispatch(setBuroNip({ code: otp, isValid: true, termsAccepted: false }));
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 404 || apiError?.status === 400) {
        setErrorMessage("Código incorrecto, intenta de nuevo");
        setOtp("");
        return;
      }
      Sentry.captureException(apiError, { extra: { component: `NipBuro-${variant}-validate`, values: { otp } } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    validateCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // Al entrar se manda el NIP (si no está validado); si ya lo estaba, se muestra el código.
  useEffect(() => {
    if (!buroNip.isValid && !resendEffectRanRef.current) {
      resendEffectRanRef.current = true;
      handleResendCode();
    }
    if (buroNip.isValid && buroNip.code) {
      setOtp(buroNip.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buroNip.isValid]);

  // El checkbox abre la confirmación del código (el modal legacy la pedía antes de consultar).
  const handleConsentChange = (checked: boolean) => {
    if (!buroNip.isValid) return;
    if (!checked) {
      dispatch(setBuroNip({ code: buroNip.code, isValid: true, termsAccepted: false }));
      return;
    }
    setModalOtp("");
    setShowConfirmModal(true);
  };

  const handleAcceptConsent = () => {
    if (!otpConfirmCorrecto) return;
    dispatch(setBuroNip({ code: buroNip.code, isValid: true, termsAccepted: true }));
    setShowConfirmModal(false);
    setModalOtp("");
  };

  /**
   * Como `ModalBuroNipPfae`: la persona es la primera de la solicitud y, si su autorización ya se mandó y
   * fue correcta, no se vuelve a consultar.
   */
  const consultarBuro = async () => {
    const persons = await getPersons({ loanApplicationId: String(application.id) }).unwrap();
    const person = persons.length > 0 ? await getPersonById(persons[0].id).unwrap() : null;
    if (person?.is_credit_bureau_authorization_pf_sended && person?.is_credit_bureau_authorization_pf_correct) {
      return;
    }
    await buroValidator(buildBuroPayloadPfae(company, application, person ? String(person.id) : "")).unwrap();
  };

  const continueToNextStatus = async () => {
    if (!nextStatus) throw new Error("No se encontró el siguiente status");

    await triggerPostNextStage({
      applicationId: application.id,
      statusCode: nextStatus.code,
      username: `TOFU - ${accountUser.email}`,
    }).unwrap();

    dispatch(setCheckSatBureau({ check_sat_bureau: checkSatBureau }));
    navigate(nextStatus.url);
  };

  const handleContinue = async () => {
    if (!buroNip.isValid || !buroNip.termsAccepted) return;

    setIsLoading(true);
    try {
      await consultarBuro();
      await continueToNextStatus();
    } catch (error) {
      const apiError = error as ApiError;
      // 409: la consulta ya se había hecho; se sigue igual que en el legacy.
      if (apiError.status === 409) {
        try {
          await continueToNextStatus();
        } catch (continueError) {
          Sentry.captureException(continueError, { extra: { component: `NipBuro-${variant}-continue` } });
          dispatch(setModalErrorVisible({ open: true, error: continueError as ApiError }));
        }
        return;
      }
      Sentry.captureException(error, { extra: { component: `NipBuro-${variant}-consulta` } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Autorización · Buró de Crédito"
        title="Autoriza la consulta a Buró de Crédito"
        subtitle="Enviamos un código de 4 dígitos por SMS y WhatsApp para autorizar la consulta."
      />

      <Box sx={{ mb: 2 }}>
        <InfoBanner
          icon={<Phone fontSize="small" />}
          label="Teléfono celular"
          value={phone.number ? `(${phone.countryCode}) ${formatPhoneNumber(phone.number)}` : "—"}
          editLabel="Cambiar"
          // Como el legacy, el teléfono del titular se corrige en el registro.
          onEdit={buroNip.isValid ? undefined : () => navigate(PATHS_TOFU_V2.registroDatosPersonales.path)}
        />
      </Box>

      <Paper
        variant="lendia_paper"
        sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, mb: 2 }}
      >
        <OtpInput length={4} value={otp} onChange={setOtp} size="lg" disabled={isLoading || buroNip.isValid} />

        {errorMessage && (
          <Box sx={{ width: "100%" }}>
            <Callout tone="warn">{errorMessage}</Callout>
          </Box>
        )}

        {rateLimited && (
          <Box sx={{ width: "100%" }}>
            <Callout tone="warn">
              Excedió el número de intentos permitidos para ese número, intenta más tarde o contacta a un asesor.
            </Callout>
          </Box>
        )}

        {buroNip.isValid ? (
          <Typography variant="body2" color="success.main" fontWeight={500}>
            Código verificado
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              ¿No recibiste el código?{" "}
              {justResent ? (
                <Box
                  component="span"
                  sx={{
                    color: "success.main",
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Check sx={{ fontSize: 14 }} /> Código reenviado
                </Box>
              ) : secondsRemaining > 0 ? (
                <Box component="span" sx={{ color: "text.disabled" }}>
                  Reenviar en {formatTime(secondsRemaining)}
                </Box>
              ) : (
                <Box
                  component="span"
                  onClick={isLoading ? undefined : handleResendCode}
                  data-testid="nip-buro-resend-button"
                  sx={{
                    color: "primary.main",
                    fontWeight: 500,
                    textDecoration: "underline",
                    cursor: isLoading ? "default" : "pointer",
                  }}
                >
                  Reenviar código
                </Box>
              )}
            </Typography>
            <Typography variant="body3" color="text.secondary">
              El código expira en 5 minutos.
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Checkbox
          checked={buroNip.termsAccepted}
          onChange={handleConsentChange}
          disabled={!buroNip.isValid || isLoading}
          label="Autorizo a Lendit Group SAPI de CV a consultar mi información ante Buró de Crédito Empresas."
        />
        <Callout tone="neutral">
          <CustomTextCiecBuro type="nipBuro" />
        </Callout>
      </Paper>

      <StepActions
        onBack={() => previousStatus && navigate(previousStatus.url)}
        onNext={handleContinue}
        nextDisabled={!buroNip.isValid || !buroNip.termsAccepted || isLoading}
        backTestId="nip-buro-back-button"
        nextTestId="nip-buro-continue-button"
      />

      <ConfirmationDialog
        open={showConfirmModal}
        title="Consulta a Buró de Crédito"
        onClose={() => setShowConfirmModal(false)}
        onAccept={handleAcceptConsent}
        closeLabel="Cancelar"
        acceptLabel="Acepto y autorizo"
        acceptDisabled={!otpConfirmCorrecto}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <CustomTextCiecBuro type="nipBuroConfirm" />
        </Typography>
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
          <Typography variant="body2" fontWeight={500} color="primary.dark" sx={{ mb: 1.5 }}>
            Confirma tu código
          </Typography>
          <OtpInput length={4} value={modalOtp} onChange={setModalOtp} />
          {modalOtp.length === 4 && !otpConfirmCorrecto && (
            <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
              El código ingresado no es correcto. Por favor verifica el código y vuelve a intentarlo.
            </Typography>
          )}
        </Box>
      </ConfirmationDialog>
    </Box>
  );
};
